/**
 * SEN-205 — grouping for set-valued cells ("500 × Retail in one icon").
 *
 * ONE rule for every set a cell shows (reference sets, option sets, a path
 * column's scalar values):
 *  - identical values share a group — identity is the value's stable KEY (a
 *    target record id, an option key, the scalar itself), never its label:
 *    two records with an equal kenning are two groups;
 *  - counts are exact (a path set's come from the server, over every record);
 *  - groups sort by count descending, then by label;
 *  - EVERY group is shown (owner: "compact the content but never discard it")
 *    — see GroupedChips for how a cell compacts instead of hiding.
 */
export interface GroupSample {
  record_id: string
  kenning: string
}

export interface ValueGroup<T> {
  key: string
  label: string
  /** One member — every member is the same value, so any one draws the group. */
  sample: T
  count: number
  /** A few of the records behind the group (the server's hover preview). */
  samples?: GroupSample[]
}

export function groupValues<T>(
  values: readonly T[],
  keyOf: (value: T, index: number) => string,
  labelOf: (value: T) => string,
  weightOf: (value: T) => number = () => 1,
): ValueGroup<T>[] {
  const byKey = new Map<string, ValueGroup<T>>()
  values.forEach((value, index) => {
    const key = keyOf(value, index)
    const group = byKey.get(key)
    if (group) group.count += weightOf(value)
    else byKey.set(key, { key, label: labelOf(value), sample: value, count: weightOf(value) })
  })
  return sortGroups([...byKey.values()])
}

export function sortGroups<T>(groups: ValueGroup<T>[]): ValueGroup<T>[] {
  return groups.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

/** records/values' grouped path answer, as a cell value (`{set, groups, total}`). */
export interface ServerGroup {
  key: string
  value: unknown
  count: number
  samples?: GroupSample[]
}

export function serverGroups(value: unknown): ServerGroup[] | null {
  if (typeof value !== 'object' || value === null) return null
  const set = value as { set?: unknown; groups?: unknown }
  return set.set === true && Array.isArray(set.groups) ? (set.groups as ServerGroup[]) : null
}
